// -*- coding: utf-8 -*-
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AssignmentTurnedIn from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import EmojiNature from '@mui/icons-material/EmojiNature';
import FactCheck from '@mui/icons-material/FactCheck';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Insights from '@mui/icons-material/Insights';
import NorthEast from '@mui/icons-material/NorthEast';
import RateReview from '@mui/icons-material/RateReview';
import WarningAmber from '@mui/icons-material/WarningAmber';

import { getTeacherHomeSummary } from './api/teacherHome';
import LibraryApprovalCard from './library/components/LibraryApprovalCard';
import { teacherTypography } from '../design/typography';


const formatDate = (value) => {
  if (!value) return '-';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? new Date(...String(value).split('-').map((part, index) => (index === 1 ? Number(part) - 1 : Number(part))))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'short',
  }).format(date);
};


const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};


const compactPreviewText = (value, fallback = '-') => {
  const normalized = String(value || '').replace(/\s+/g, ' ').trim();
  return normalized || fallback;
};


const splitMultilineText = (value) => String(value || '')
  .split(/\r?\n/)
  .map((line) => line.trim())
  .filter(Boolean);


const compactEmptyStateSx = (tone) => {
  const palette = {
    success: {
      color: '#2E7D32',
      backgroundColor: '#F1F8F2',
      borderColor: 'rgba(46, 125, 50, 0.10)',
    },
    info: {
      color: '#1976D2',
      backgroundColor: '#EEF6FF',
      borderColor: 'rgba(25, 118, 210, 0.10)',
    },
    neutral: {
      color: '#5F6B66',
      backgroundColor: '#F6F8F7',
      borderColor: 'rgba(95, 107, 102, 0.10)',
    },
  };

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    py: 1.1,
    px: 1.35,
    borderRadius: 2,
    border: `1px solid ${palette[tone].borderColor}`,
    backgroundColor: palette[tone].backgroundColor,
    color: palette[tone].color,
  };
};


const formatInsightDayLabel = (value, referenceDate) => {
  if (!value) return '-';

  const parseDateOnly = (rawValue) => {
    if (!rawValue || !/^\d{4}-\d{2}-\d{2}$/.test(String(rawValue))) return null;
    const [year, month, day] = String(rawValue).split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const targetDate = parseDateOnly(value);
  const refDate = parseDateOnly(referenceDate);

  if (targetDate && refDate) {
    const diffDays = Math.round((refDate.getTime() - targetDate.getTime()) / 86400000);
    if (diffDays === 0) return 'Bugün';
    if (diffDays === 1) return 'Dün';
  }

  return formatDate(value);
};


const TeacherHomepage = ({ schoolId, onError, onSelectTab, onOpenObservationJournal, onOpenHomework, showLibraryApprovals = true }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!schoolId) return;
      setLoading(true);
      setLocalError('');
      try {
        const payload = await getTeacherHomeSummary(schoolId);
        if (cancelled) return;
        setSummary(payload);
        setSelectedClassId(String(payload?.selected_class?.id || ''));
      } catch (_) {
        if (cancelled) return;
        const message = 'Öğretmen anasayfası yüklenemedi.';
        setLocalError(message);
        onError?.(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [schoolId, onError]);

  const handleClassChange = async (event) => {
    const nextClassId = String(event.target.value || '');
    setSelectedClassId(nextClassId);
    setLoading(true);
    setLocalError('');
    try {
      const payload = await getTeacherHomeSummary(schoolId, { classId: nextClassId });
      setSummary(payload);
      setSelectedClassId(String(payload?.selected_class?.id || nextClassId));
    } catch (_) {
      const message = 'Sınıf özeti güncellenemedi.';
      setLocalError(message);
      onError?.(message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (item) => {
    if (!item) return;
    if (item.target_path) {
      navigate(item.target_path);
      return;
    }
    if (item.target_tab !== undefined && item.target_tab !== null) {
      if (Number(item.target_tab) === 12 && onOpenHomework) {
        onOpenHomework(item.class_id ?? selectedClassId);
        return;
      }
      onSelectTab?.(item.target_tab);
    }
  };

  const openObservationSuggestion = (item) => {
    if (!item) return;
    if (onOpenObservationJournal) {
      onOpenObservationJournal({ classId: item.class_id, studentId: item.student_id });
      return;
    }
    onSelectTab?.(30);
  };

  const openFeedbackPanel = () => {
    onSelectTab?.(29);
  };

  if (loading && !summary) {
    return (
      <Stack spacing={2.5}>
        <Skeleton variant="rounded" height={152} />
        <Skeleton variant="rounded" height={88} />
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={260} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={260} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
        </Grid>
      </Stack>
    );
  }

  const availableClasses = summary?.available_classes || [];
  const showClassSelector = availableClasses.length > 1;
  const selectedClass = summary?.selected_class;
  const topStripItems = (summary?.top_strip?.items || []).slice().sort((a, b) => {
    const aPriority = a?.key === 'attendance_missing_today' ? 1 : 0;
    const bPriority = b?.key === 'attendance_missing_today' ? 1 : 0;
    return aPriority - bPriority;
  });
  const homeworkItems = summary?.homework_backlog?.items || [];
  const feedbackItems = summary?.recent_feedback?.items || [];
  const weeklyMetrics = (summary?.weekly_summary?.metrics || []).filter((metric) => metric?.key !== 'study_minutes');
  const weeklySummarySignals = summary?.weekly_summary?.signals || {};
  const observationSuggestions = summary?.observation_suggestions?.items || [];
  const weeklyInsights = summary?.weekly_insights?.items || [];
  const weekWindow = summary?.weekly_summary
    ? `${formatDate(summary.weekly_summary.week_start)} - ${formatDate(summary.weekly_summary.week_end)}`
    : '';
  const weeklySummarySource = summary?.weekly_summary?.source || '';
  const weeklySummaryText = String(summary?.weekly_summary?.message_text || '').trim();
  const weeklySummaryLines = splitMultilineText(weeklySummaryText);
  const participation = summary?.activity_participation;
  const weeklySummaryMetrics = weeklyMetrics.map((metric) => metric.key === 'active_students' && participation
    ? { ...metric, value: participation.active_students } : metric);
  const weeklySummaryHighlights = [
    summary?.weekly_summary?.highlights?.best_reader_name && summary?.weekly_summary?.highlights?.best_reader_name !== '-'
      ? `Kitap Kurdu: ${summary.weekly_summary.highlights.best_reader_name}`
      : '',
    summary?.weekly_summary?.highlights?.best_question_name && summary?.weekly_summary?.highlights?.best_question_name !== '-'
      ? `Soru Canavarı: ${summary.weekly_summary.highlights.best_question_name}`
      : '',
    summary?.weekly_summary?.highlights?.best_star_name && summary?.weekly_summary?.highlights?.best_star_name !== '-'
      ? `Yıldız Avcısı: ${summary.weekly_summary.highlights.best_star_name}`
      : '',
  ].filter(Boolean);
  const primaryUrgentItem = topStripItems[0] || null;
  const hasUrgentItem = Boolean(primaryUrgentItem);

  const primaryAction = (() => {
    if (!primaryUrgentItem) {
      return { summary: 'Bugün bekleyen kritik konu yok.' };
    }

    if (primaryUrgentItem.key === 'homework_backlog') {
      return {
        label: 'Bekleyenleri Gör',
        targetTab: primaryUrgentItem.target_tab ?? 20,
        summary: primaryUrgentItem.value === 1
          ? 'Bugün ödev tarafında bir işlem bekliyor.'
          : `${primaryUrgentItem.value} işlem bekliyor.`,
      };
    }

    if (primaryUrgentItem.key === 'attendance_missing_today') {
      return {
        label: 'Yoklama Al',
        targetTab: primaryUrgentItem.target_tab ?? 1,
        summary: primaryUrgentItem.description || 'Bugünün yoklama kaydı henüz girilmedi.',
      };
    }

    if (primaryUrgentItem.key === 'homework_missing_today') {
      return {
        label: 'Ödev Gir',
        targetTab: primaryUrgentItem.target_tab ?? 12,
        summary: primaryUrgentItem.description || 'Bugün için henüz yeni ödev girilmedi.',
      };
    }

    if (primaryUrgentItem.key === 'observation_suggestion') {
      return {
        label: 'Gözlem Gir',
        targetTab: primaryUrgentItem.target_tab ?? 30,
        summary: primaryUrgentItem.description || 'Gözlem girişi için önerilen öğrenciler var.',
      };
    }

    if (primaryUrgentItem.key === 'recent_feedback') {
      return {
        label: 'Değer Geri Bildirimlerini Gör',
        targetTab: primaryUrgentItem.target_tab ?? 29,
        summary: `${primaryUrgentItem.value} değer çalışması geri bildirimi var.`,
      };
    }

    if (primaryUrgentItem.key === 'attendance_weekly') {
      return {
        label: 'Yoklamayı Gör',
        targetTab: primaryUrgentItem.target_tab ?? 1,
        summary: primaryUrgentItem.description || 'Yoklama tarafında dikkat isteyen bir konu var.',
      };
    }

    return {
      label: 'Detayı Gör',
      targetTab: primaryUrgentItem.target_tab,
      summary: primaryUrgentItem.description || 'Bugün dikkat isteyen bir konu var.',
    };
  })();

  const weeklyMetricLabelMap = {
    active_students: 'Aktif öğrenci',
    questions: 'Çözülen soru',
    completed_books: 'Biten kitap',
    observations: 'Gözlem sayısı',
    stars: 'Yıldız sayısı',
    reading_speed: 'Okuma hızı paylaşımı',
    absent: 'Devamsızlık',
    late: 'Geç kalma',
    not_healthy_nutrition: 'Sağlıksız beslenme',
    missing_material: 'Eksik materyal',
    homework_missing: 'Eksik ödev',
    homework_not_done: 'Yapılmayan ödev',
  };

  const weeklyMetricValue = (metric) => {
    if (metric.key === 'reading_speed') {
      return `${metric.value}`;
    }
    if (metric.total !== undefined) {
      return `${metric.value}/${metric.total}`;
    }
    return `${metric.value}`;
  };

  const vksPrimaryLabel = 'Bugün aktif veli';
  const vksPrimaryValue = summary?.vks_summary?.today_active_parents_count ?? 0;
  const vksSecondaryValue = summary?.vks_summary?.progress_pct ?? 0;
  const urgentStripTone = primaryUrgentItem ? '#FFF6E8' : '#F5F8F6';

  return (
    <Stack spacing={2.5} data-testid="teacher-homepage-typography" sx={{ containerType: 'inline-size', containerName: 'library', fontSynthesis: 'none' }}>
      {localError ? <Alert severity="error">{localError}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.75, md: 2.25 },
          borderRadius: 3,
          color: '#112A21',
          background: 'linear-gradient(135deg, #E9F7EF 0%, #F6EBCF 100%)',
          border: '1px solid rgba(17, 42, 33, 0.08)',
        }}
      >
        <Stack spacing={1.35}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.35}
          >
            <Box>
              <Typography variant="h5" sx={{ ...teacherTypography.pageTitle, color: '#112A21', mb: 0.35 }}>
                Öğretmen Anasayfası
              </Typography>
              <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14, color: '#365148' }}>
                Sınıfındaki durumu gör, önce neye bakacağını hızlıca seç.
              </Typography>
            </Box>

            {showClassSelector ? (
              <FormControl size="small" sx={{ minWidth: 180, bgcolor: 'rgba(255,255,255,0.72)', borderRadius: 2 }}>
                <Select value={selectedClassId} onChange={handleClassChange} displayEmpty>
                  {availableClasses.map((classItem) => (
                    <MenuItem key={classItem.id} value={String(classItem.id)}>
                      {classItem.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Sınıf: ${selectedClass?.code || '-'}`} sx={{ bgcolor: 'rgba(255,255,255,0.8)' }} />
            <Chip label={`Öğrenci: ${selectedClass?.student_count || 0}`} sx={{ bgcolor: 'rgba(255,255,255,0.8)' }} />
            <Chip label={`Hafta: ${weekWindow || '-'}`} sx={{ bgcolor: 'rgba(255,255,255,0.8)' }} />
          </Stack>

          {showLibraryApprovals && <LibraryApprovalCard schoolId={schoolId} />}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.35, md: 1.6 },
              borderRadius: 2.5,
              border: '1px solid rgba(17, 42, 33, 0.08)',
              background: 'rgba(255,255,255,0.82)',
            }}
          >
            <Stack spacing={1.3}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Insights sx={{ color: '#003049', fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" sx={{ ...teacherTypography.cardTitle, color: '#16392C' }}>
                      Haftalık Sınıf Özeti
                    </Typography>
                    <Typography variant="caption" sx={teacherTypography.metadata}>
                      {weekWindow || '-'}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              {weeklySummarySource === 'snapshot' && weeklySummaryLines.length ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: '#F8FBF9',
                    border: '1px solid rgba(17, 42, 33, 0.06)',
                  }}
                >
                  <Stack spacing={0.75}>
                    {weeklySummaryLines.map((line, index) => (
                      <Typography
                        key={`${line}-${index}`}
                        variant="body2"
                        sx={{
                          color: index < 2 ? '#16392C' : '#244235',
                          fontWeight: index < 2 ? 600 : 500,
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                           overflowWrap: 'anywhere',
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              ) : (
                <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                  Haftalık sınıf özeti hazır olduğunda burada görünecek.
                </Typography>
              )}

              {weeklySummaryMetrics.length ? (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {weeklySummaryMetrics.map((metric) => (
                    <Chip
                      key={metric.key}
                      size="small"
                      clickable={metric.key === 'active_students'}
                      onClick={metric.key === 'active_students' ? () => {
                        const query = new URLSearchParams({
                          class_id: String(selectedClass.id),
                          start_date: summary.weekly_summary.week_start,
                          end_date: summary.weekly_summary.week_end,
                          filter: 'active',
                          expected_count: String(metric.value),
                          summary_source: weeklySummarySource,
                        });
                        navigate(`/admin/ogrenci-etkinlikleri?${query.toString()}`);
                      } : undefined}
                      label={`${weeklyMetricLabelMap[metric.key] || metric.label}: ${weeklyMetricValue(metric)}${metric.key === 'active_students' ? ' ›' : ''}`}
                      sx={{ bgcolor: '#F5F8F6', ...(metric.key === 'active_students' ? {
                        minHeight: 44, fontWeight: 700, border: '1px solid #D8EAE3',
                        '&:focus-visible': { outline: '3px solid #1E40AF', outlineOffset: 2 },
                      } : {}) }}
                    />
                  ))}
                </Stack>
              ) : null}

              {participation && participation.snapshot_active_students !== null
                && participation.snapshot_active_students !== participation.active_students ? (
                  <Typography variant="body2" sx={teacherTypography.supportingText}>
                    Haftalık rapor oluşturulduğunda {participation.snapshot_active_students} aktif öğrenci vardı.
                    {' '}Yukarıdaki katılım sayısı aynı dönem için güncel sınıf üyeliği ve kayıtlara göre {participation.active_students} öğrencidir.
                  </Typography>
                ) : null}

              {weeklySummaryHighlights.length ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.05,
                    borderRadius: 2,
                    backgroundColor: '#FCF9F2',
                    border: '1px solid rgba(155, 110, 42, 0.10)',
                  }}
                >
                  <Typography variant="caption" sx={{ ...teacherTypography.metadata, lineHeight: 1.6 }}>
                    {weeklySummaryHighlights.join(' • ')}
                  </Typography>
                </Paper>
              ) : null}

              {weeklySummarySignals.positive_text || weeklySummarySignals.risk_text || weeklySummarySignals.action_text ? (
                <Stack spacing={0.6}>
                  {weeklySummarySignals.positive_text ? (
                    <Box
                      sx={{
                        px: 1.15,
                        py: 0.9,
                        borderRadius: 2,
                        backgroundColor: '#EEF7F1',
                        color: '#1E5B45',
                        border: '1px solid rgba(30, 91, 69, 0.10)',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}>
                        {weeklySummarySignals.positive_text}
                      </Typography>
                    </Box>
                  ) : null}
                  {weeklySummarySignals.risk_text ? (
                    <Box
                      sx={{
                        px: 1.15,
                        py: 0.9,
                        borderRadius: 2,
                        backgroundColor: '#FFF6E8',
                        color: '#9C4A00',
                        border: '1px solid rgba(156, 74, 0, 0.10)',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}>
                        {weeklySummarySignals.risk_text}
                      </Typography>
                    </Box>
                  ) : null}
                  {weeklySummarySignals.action_text ? (
                    <Box
                      sx={{
                        px: 1.15,
                        py: 0.9,
                        borderRadius: 2,
                        backgroundColor: '#EEF6FF',
                        color: '#205493',
                        border: '1px solid rgba(32, 84, 147, 0.10)',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}>
                        {weeklySummarySignals.action_text}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.2, md: 1.35 },
              borderRadius: 2.5,
              border: '1px solid rgba(17, 42, 33, 0.08)',
              background: urgentStripTone,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1.25}
            >
              <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <WarningAmber sx={{ color: primaryUrgentItem ? '#B26400' : '#5F6B66', fontSize: 20 }} />
                  <Typography variant="body2" sx={teacherTypography.cardTitle}>
                    Bugün / Öncelik
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.5, color: '#17213C' }}>
                  {primaryAction.summary}
                </Typography>
                {topStripItems.length ? (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {topStripItems.map((item) => (
                      <Chip
                        key={item.key}
                        size="small"
                        label={item.key === 'recent_feedback'
                          ? `${item.value} değer geri bildirimi`
                          : item.key === 'homework_backlog'
                            ? `${item.value} işlem bekliyor`
                            : item.description}
                        sx={{ bgcolor: 'rgba(255,255,255,0.75)' }}
                      />
                    ))}
                  </Stack>
                ) : null}
              </Stack>

              {hasUrgentItem ? (
                <Button
                  variant="contained"
                  onClick={() => primaryUrgentItem?.key === 'homework_missing_today' && onOpenHomework ? onOpenHomework(selectedClassId) : primaryAction.targetTab !== undefined && onSelectTab?.(primaryAction.targetTab)}
                  sx={{
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    borderRadius: 999,
                    px: 2.2,
                    py: 0.95,
                    fontWeight: 700,
                    textTransform: 'none',
                    backgroundColor: '#1E5B45',
                    '&:hover': {
                      backgroundColor: '#174735',
                    },
                  }}
                >
                  {primaryAction.label}
                </Button>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </Paper>

      {topStripItems.length ? (
        <Grid container spacing={1.5}>
          {topStripItems.map((item) => (
            <Grid item xs={12} md={4} key={item.key}>
              <Paper
                elevation={0}
                onClick={() => handleQuickAction(item)}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: '1px solid rgba(17, 42, 33, 0.08)',
                  cursor: 'pointer',
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 24px rgba(17, 42, 33, 0.08)',
                  },
                }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="overline" sx={{ ...teacherTypography.metricLabel, letterSpacing: 0.45 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="h5" sx={{ ...teacherTypography.kpiValue, color: '#17213C', lineHeight: 1.1 }}>
                      {item.value}
                    </Typography>
                    <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14, mt: 0.5 }}>
                      {item.description}
                    </Typography>
                  </Box>
                  <NorthEast fontSize="small" sx={{ color: 'text.secondary' }} />
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : null}

      {!selectedClass ? (
        <Alert severity="info">Bu öğretmen için atanmış sınıf bulunamadı.</Alert>
      ) : null}

      {selectedClass ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2.5,
          }}
        >
          {homeworkItems.length ? (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: '1px solid rgba(17, 42, 33, 0.08)',
                background: 'linear-gradient(180deg, rgba(246, 235, 207, 0.22) 0%, #FFFFFF 20%)',
                height: '100%',
              }}
            >
              <Stack spacing={2} height="100%">
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                     alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.25}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <AssignmentTurnedIn sx={{ color: '#9C4A00' }} />
                    <Box>
                      <Typography variant="h6" sx={teacherTypography.sectionTitle}>Bekleyen Ödev Durumları</Typography>
                      <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                        Son {summary?.homework_backlog?.window_days || 14} günde durum girişi bekleyen ödevler
                      </Typography>
                    </Box>
                  </Stack>
                  <Button size="small" sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, minHeight: 44, fontWeight: 600 }} onClick={() => onSelectTab?.(20)}>Ödev Durumlarını Gör</Button>
                </Stack>

                <Stack spacing={1.25}>
                  {homeworkItems.map((item) => (
                    <Paper key={item.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#FFF9ED' }}>
                      <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ minWidth: 0 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ ...teacherTypography.cardTitle, overflowWrap: 'anywhere' }}>{item.lesson}</Typography>
                          <Typography
                            variant="body2"
                            sx={{ ...teacherTypography.supportingText, fontSize: 14,
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                               overflow: 'hidden',
                               overflowWrap: 'anywhere',
                            }}
                          >
                            {compactPreviewText(item.description, 'Açıklama eklenmedi')}
                          </Typography>
                        </Box>
                        <Stack spacing={0.15} alignItems="flex-end" sx={{ minWidth: 72, flexShrink: 0 }}>
                          <Typography variant="body2" sx={teacherTypography.kpiValue}>
                            {item.pending_count}/{item.total_count}
                          </Typography>
                          <Typography variant="caption" sx={{ ...teacherTypography.metadata, whiteSpace: 'nowrap' }}>
                            Son tarih
                          </Typography>
                          <Typography variant="caption" sx={{ ...teacherTypography.metadata, whiteSpace: 'nowrap' }}>
                            {formatDate(item.due_date || item.assigned_at)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          {feedbackItems.length ? (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(17, 42, 33, 0.08)', height: '100%' }}>
              <Stack spacing={2} height="100%">
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                     alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.25}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <FactCheck sx={{ color: '#245C4A' }} />
                    <Box>
                      <Typography variant="h6" sx={teacherTypography.sectionTitle}>Veli Değer Çalışması Geri Bildirimleri</Typography>
                      <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                        Seçili sınıfta kayda geçen son 3 değer çalışması geribildirimi
                      </Typography>
                      <Typography variant="caption" sx={teacherTypography.metadata}>
                        Her satır Değerler ekranına açılır
                      </Typography>
                    </Box>
                  </Stack>
                  <Button size="small" sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, minHeight: 44, fontWeight: 600 }} onClick={openFeedbackPanel}>Geribildirimleri Gör</Button>
                </Stack>

                <Stack spacing={1.25}>
                  {feedbackItems.map((item) => (
                    <Paper
                      key={item.id}
                      component="button"
                      type="button"
                      onClick={openFeedbackPanel}
                      elevation={0}
                      sx={{
                        width: '100%',
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#EEF7F3',
                        border: '1px solid rgba(36, 92, 74, 0.08)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 8px 18px rgba(17, 42, 33, 0.08)',
                          borderColor: 'rgba(36, 92, 74, 0.18)',
                        },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ minWidth: 0 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={teacherTypography.studentName}>{item.student_name}</Typography>
                          <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                            {item.parent_name} • {item.theme_title}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.35}>
                          <Typography variant="caption" sx={teacherTypography.metadata}>
                            {formatDateTime(item.submitted_at)}
                          </Typography>
                          <NorthEast fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(17, 42, 33, 0.08)', height: '100%' }}>
            <Stack spacing={1.75}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <RateReview sx={{ color: '#6B4E16' }} />
                <Box>
                  <Typography variant="h6" sx={teacherTypography.sectionTitle}>Gözlem Günlüğü İçin Önerilen Öğrenciler</Typography>
                  <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                    Bu öğrenciler için yakın zamanda gözlem eklenmemiş. Tek tıkla gözlem alanına açılır.
                  </Typography>
                </Box>
              </Stack>

              {observationSuggestions.length ? (
                <Stack spacing={1.1}>
                  {observationSuggestions.map((item) => (
                    <Paper
                      key={`${item.class_id}-${item.student_id}`}
                      elevation={0}
                      sx={{ p: 1.4, borderRadius: 2.25, bgcolor: '#FBFAF5', border: '1px solid rgba(17, 42, 33, 0.06)' }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1.25}
                        sx={{ minWidth: 0 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body1" sx={teacherTypography.studentName}>
                            {item.student_name}
                          </Typography>
                          <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                            {item.last_observed_at
                              ? `Son gözlem: ${formatDateTime(item.last_observed_at)}`
                              : 'Henüz gözlem yok'}
                          </Typography>
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => openObservationSuggestion(item)}>
                          Gözlem Ekle
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box role="status" aria-atomic="true" sx={compactEmptyStateSx('success')}>
                  <CheckCircleOutline fontSize="small" aria-hidden="true" sx={{ flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
                    Seçili sınıfta gözlem önerisi gerektiren öğrenci görünmüyor.
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>

          {summary?.vks_summary?.has_data ? (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(17, 42, 33, 0.08)', height: '100%' }}>
              <Stack spacing={2} height="100%">
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                     alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.25}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <EmojiNature sx={{ color: '#2D6A4F' }} />
                    <Box>
                      <Typography variant="h6" sx={teacherTypography.sectionTitle}>VKS Özeti</Typography>
                      <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                        Seçili sınıfın veli katılım ritmi
                      </Typography>
                    </Box>
                  </Stack>
                  <Button size="small" sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, minHeight: 44, fontWeight: 600 }} onClick={() => navigate('/admin/vks/gelecek-ormani')}>VKS Özetini Gör</Button>
                </Stack>

                <Stack spacing={1.25}>
                  <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2.5, bgcolor: '#F1F8F4' }}>
                    <Typography variant="caption" sx={teacherTypography.metricLabel}>{vksPrimaryLabel}</Typography>
                    <Typography variant="h4" sx={{ ...teacherTypography.kpiValue, lineHeight: 1.12 }}>{vksPrimaryValue}</Typography>
                    <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                      Katılan veli: {summary.vks_summary.participant_parent_count || 0}
                    </Typography>
                  </Paper>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Paper elevation={0} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#F7FBF8', height: '100%' }}>
                        <Typography variant="caption" sx={teacherTypography.metadata}>Aylık ilerleme</Typography>
                        <Typography variant="body1" sx={teacherTypography.kpiValue}>%{vksSecondaryValue}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper elevation={0} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#F7FBF8', height: '100%' }}>
                        <Typography variant="caption" sx={teacherTypography.metadata}>Bugün eklenen</Typography>
                        <Typography variant="body1" sx={teacherTypography.kpiValue}>{summary.vks_summary.drops_added_today || 0} damla</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                    Bu ay {summary.vks_summary.drops}/{summary.vks_summary.target} damla toplandı • Faz: {summary.vks_summary.phase || '-'}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          ) : null}

        </Box>
      ) : null}

      {selectedClass ? (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(17, 42, 33, 0.08)' }}>
          <Stack spacing={1.75}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                   alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.25}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Insights sx={{ color: '#6A4C00' }} />
                <Box>
                  <Typography variant="h6" sx={teacherTypography.sectionTitle}>Bu Haftadan Kısa Notlar</Typography>
                  <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                    Seçili sınıfta bu hafta kayda geçen son 10 hareket
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="caption" sx={{ ...teacherTypography.metadata, textAlign: { xs: 'left', sm: 'right' } }}>
                Her satır ilgili ekrana açılır
              </Typography>
            </Stack>

            {weeklyInsights.length ? (
              <Stack spacing={1}>
                {weeklyInsights.map((item) => (
                  <Paper
                    key={item.id}
                    elevation={0}
                    onClick={() => item.target_tab !== undefined && onSelectTab?.(item.target_tab)}
                    sx={{
                      py: 1.1,
                      px: 1.25,
                      borderRadius: 2,
                      bgcolor: '#FCFCF8',
                      border: '1px solid rgba(17, 42, 33, 0.06)',
                      cursor: item.target_tab !== undefined ? 'pointer' : 'default',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                      <Chip
                        size="small"
                        label={formatInsightDayLabel(item.occurred_on, summary?.weekly_insights?.week_end)}
                        sx={{ bgcolor: '#F2EFD9', fontWeight: 700 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.message}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Box sx={compactEmptyStateSx('neutral')}>
                <InfoOutlined fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Bu hafta kayda geçen yeni bir kısa not yok.
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      ) : null}

      {loading ? <Skeleton variant="rounded" height={80} /> : null}
    </Stack>
  );
};


export default TeacherHomepage;
